import React, { useMemo } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ButtonComp,
  CardComp,
  TextInputComp,
} from "../../../components";
import { PageTitle } from "../modules/PageHeaders";
import RoleFormSkeletonLoader from "../../../components/Common/RoleFormSkeletonLoader";
import {
  useGetLoanApplicationSettingsQuery,
  useUpdateLoanApplicationSettingsMutation,
} from "../../../store/admin/loanApplicationSettingsSlice";

const LOAN_TYPE_SETTINGS_PATH = "/super_admin/loan_management/loan_type";

const validationSchema = Yup.object({
  borrowing_multiplier: Yup.number()
    .typeError("Enter a valid multiplier")
    .required("Required")
    .min(0.01, "Must be greater than 0")
    .max(100, "Must be 100 or less"),
  allow_on_loan_guarantors: Yup.boolean().required(),
  allow_partial_repayment: Yup.boolean(),
  min_repayment_percent: Yup.number()
    .nullable()
    .transform((value, original) =>
      original === "" || original == null ? null : Number(original)
    )
    .when("allow_on_loan_guarantors", {
      is: true,
      then: (schema) =>
        schema
          .required("Required when on-loan guarantors are allowed")
          .min(0, "Min 0")
          .max(100, "Max 100"),
      otherwise: (schema) => schema.nullable(),
    }),
});

export default function LoanApplicationSetting() {
  const { data, isLoading, isFetching, isError, error, refetch } =
    useGetLoanApplicationSettingsQuery();
  const [updateSettings, { isLoading: isSaving }] =
    useUpdateLoanApplicationSettingsMutation();

  const initialValues = useMemo(
    () => ({
      borrowing_multiplier: data?.borrowing_multiplier ?? 2,
      allow_on_loan_guarantors: Boolean(data?.allow_on_loan_guarantors),
      allow_partial_repayment: Boolean(data?.allow_partial_repayment),
      min_repayment_percent:
        data?.min_repayment_percent == null ? "" : data.min_repayment_percent,
    }),
    [data]
  );

  const handleSubmit = async (values) => {
    try {
      const payload = {
        borrowing_multiplier: Number(values.borrowing_multiplier),
        allow_on_loan_guarantors: Boolean(values.allow_on_loan_guarantors),
        allow_partial_repayment: Boolean(values.allow_partial_repayment),
        min_repayment_percent: values.allow_on_loan_guarantors
          ? Number(values.min_repayment_percent)
          : null,
      };
      const response = await updateSettings(payload);
      if (response?.error) {
        return toast.error(
          response.error?.data?.message || "Unable to save loan settings"
        );
      }
      if (response?.data?.status === false) {
        return toast.error(response.data?.message || "Unable to save loan settings");
      }
      toast.success(response?.data?.message || "Loan settings saved");
    } catch {
      toast.error("Unable to save loan settings");
    }
  };

  if (isLoading) {
    return (
      <section className="px-2 px-lg-4 min-vh-100 pb-5 mb-5">
        <div className="mt-1">
          <PageTitle title="Loan Application Settings" />
        </div>
        <RoleFormSkeletonLoader />
      </section>
    );
  }

  return (
    <section className="px-2 px-lg-4 min-vh-100 pb-5 mb-5">
      <div className="mt-1">
        <PageTitle title="Loan Application Settings" />
      </div>

      <CardComp
        cardClassName="rounded-4 shadow3 border-0 mt-3 mb-4"
        cardBodyClassName="py-4"
      >
        <p className="mb-3 text-muted" style={{ fontSize: "15px", lineHeight: 1.6 }}>
          Configure how much members can borrow from their savings and shares, and
          whether members with disbursed loans may act as guarantors.
        </p>
        <p className="mb-0 small">
          <span className="text-muted">Interest and repayment schedules live on </span>
          <Link
            to={LOAN_TYPE_SETTINGS_PATH}
            className="fw-semibold text-decoration-none"
            style={{ color: "var(--color-secondary-readable)" }}
          >
            Loan Types
          </Link>
          <span className="text-muted"> (optional multiplier override per type).</span>
        </p>
      </CardComp>

      {isError && (
        <div className="alert alert-danger">
          {error?.data?.message || error?.message || "Could not load loan settings."}
          <button className="btn btn-link" type="button" onClick={() => refetch()}>
            Retry
          </button>
        </div>
      )}

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, setFieldValue, isSubmitting, resetForm, dirty }) => (
          <Form className="d-flex flex-column gap-4" aria-busy={isFetching}>
            <CardComp cardClassName="rounded-4 shadow3 border-0" cardBodyClassName="py-4">
              <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
                Borrowing power
              </h5>
              <p className="text-muted small mb-4">
                Eligible amount = (savings + shares) × multiplier, then capped by the
                loan type maximum. Loan types may override this multiplier.
              </p>
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <TextInputComp
                    LabelText="Global multiplier (e.g. 2 or 3)"
                    LabelClassName="fw-bold"
                    name="borrowing_multiplier"
                    type="number"
                    placeholder="2"
                    required
                  />
                </div>
              </div>
            </CardComp>

            <CardComp cardClassName="rounded-4 shadow3 border-0" cardBodyClassName="py-4">
              <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
                Guarantor eligibility
              </h5>
              <p className="text-muted small mb-4">
                Members with disbursed loans are shown but disabled unless you allow
                them and they meet the repayment threshold.
              </p>

              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="allow_on_loan_guarantors"
                  checked={Boolean(values.allow_on_loan_guarantors)}
                  onChange={(e) => {
                    setFieldValue("allow_on_loan_guarantors", e.target.checked);
                    if (!e.target.checked) {
                      setFieldValue("min_repayment_percent", "");
                    }
                  }}
                />
                <label className="form-check-label fw-semibold" htmlFor="allow_on_loan_guarantors">
                  Allow members with a disbursed loan to guarantee if they meet repayment %
                </label>
              </div>

              {values.allow_on_loan_guarantors && (
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <TextInputComp
                      LabelText="Minimum repayment percent"
                      LabelClassName="fw-bold"
                      name="min_repayment_percent"
                      type="number"
                      placeholder="e.g. 80"
                      required
                      desc="Candidate must have repaid at least this % on every active disbursed loan."
                    />
                  </div>
                </div>
              )}
            </CardComp>

            <CardComp cardClassName="rounded-4 shadow3 border-0" cardBodyClassName="py-4">
              <h5 className="fw-bold mb-1" style={{ color: "#383838" }}>
                Repayments
              </h5>
              <p className="text-muted small mb-4">
                Let members pay any amount towards an installment instead of the whole
                amount at once. Money paid beyond an installment goes to the next ones,
                and the smallest payment is ₦100. Each loan type can override this.
              </p>

              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="allow_partial_repayment"
                  checked={Boolean(values.allow_partial_repayment)}
                  onChange={(e) =>
                    setFieldValue("allow_partial_repayment", e.target.checked)
                  }
                />
                <label className="form-check-label fw-semibold" htmlFor="allow_partial_repayment">
                  Allow part payments on loan installments
                </label>
              </div>
            </CardComp>

            <div className="d-flex flex-column flex-sm-row justify-content-end gap-3 pt-2 pb-4">
              <ButtonComp
                type="button"
                btnText="Reset"
                containerClassName="w-auto"
                btnClassName="px-4 py-3 rounded border bg-white text-secondary"
                onClick={() => resetForm()}
                disabled={isSubmitting || isSaving || !dirty}
              />
              <ButtonComp
                type="submit"
                btnText={isSaving || isSubmitting ? "Saving…" : "Save settings"}
                loading={isSaving || isSubmitting}
                containerClassName="w-auto"
                btnClassName="bg-primary1 text-white px-5 py-3 rounded border-0 shadow-sm"
                ButtonInlineStyle={{ minWidth: "220px" }}
              />
            </div>
          </Form>
        )}
      </Formik>
    </section>
  );
}
